import React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Heart, Star } from 'lucide-react';

// Inline minimal SVG for tombstone to avoid lucide-react dependency
function TombstoneSVG({ width = 16, height = 16, className, style, title, ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : 'presentation'}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M6 21v-7a6 6 0 0 1 12 0v7" />
      <path d="M4 21h16" />
    </svg>
  );
}

const INTERNAL_ICON_LIBRARY = {
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  heart: Heart,
  'heart-filled': Heart,
  'heart-outline': Heart,
  'heart-pulse': Heart,
  star: Star,
  'star-outline': Star,
  'star-filled': Star,
};

const ICON_LIBRARIES = {
  internal: INTERNAL_ICON_LIBRARY,
  lucide: INTERNAL_ICON_LIBRARY,
};

export default function CustomIcon({
  name,
  lib = 'internal',
  size = 16,
  className,
  style,
  title,
  ...a11y
}) {
  const restA11y = { ...a11y };
  const hasAriaHiddenProp = Object.prototype.hasOwnProperty.call(restA11y, 'aria-hidden');
  const ariaHiddenProp = hasAriaHiddenProp ? restA11y['aria-hidden'] : undefined;
  if (hasAriaHiddenProp) {
    delete restA11y['aria-hidden'];
  }
  const hasTitle = title !== null && title !== undefined && `${title}`.length > 0;
  const ariaLabelValue = restA11y['aria-label'];
  const ariaLabelledByValue = restA11y['aria-labelledby'];
  const hasAriaLabel =
    (ariaLabelValue !== undefined && ariaLabelValue !== null && `${ariaLabelValue}`.length > 0) ||
    (ariaLabelledByValue !== undefined && ariaLabelledByValue !== null && `${ariaLabelledByValue}`.length > 0);
  const shouldHide = ariaHiddenProp !== undefined ? ariaHiddenProp : !(hasTitle || hasAriaLabel);

  if (name === 'tombstone') {
    return (
      <TombstoneSVG
        width={size}
        height={size}
        className={className}
        style={{ fontSize: size, lineHeight: 1, display: 'inline-flex', ...style }}
        title={title}
        aria-hidden={shouldHide}
        {...restA11y}
      />
    );
  }

  const library = ICON_LIBRARIES[lib];
  if (!library) {
    return null;
  }

  const IconComponent = library[name];
  if (!IconComponent) {
    return null;
  }

  return (
    <span
      className={className}
      style={{ fontSize: size, lineHeight: 1, display: 'inline-flex', ...style }}
      title={title}
      aria-hidden={shouldHide}
      {...restA11y}
    >
      <IconComponent size={size} focusable="false" aria-hidden={shouldHide} title={title} />
    </span>
  );
}
